import {Component, Input, OnInit} from '@angular/core';
import {IPreConsentPlanMappedItem, PreConsentPlansLogicService} from '@app/logic/pre-consent-plans';
import {
    DOCUMENT_GROUP_CODES_CONST,
    DOCUMENT_REQUIRED_TYPE_ENUM,
    IDefaultSystemAreaDocumentTypeDto, IDocumentUploadDto, LotContractTypeEnumId,
    SYSTEM_AREA_ENUM
} from '@classictechsolutions/hubapi-transpiled-enums';
import {LotDesignPermissions} from '@app/core/permissions';
import {Debounce} from '@app/shared/decorators/debounce.decorator';
import {UntypedFormGroup} from '@angular/forms';
import {DefaultSystemAreaDocumentTypesLogicService} from '@app/logic/default-system-area-document-types';
import {
    IRequestPreConsentPlanDocumentsToUpload
} from '@app/views/lot/design/pre-consent/request-preconsent-dialog-wizard-steps/i.request-pre-consent-plan-documents-to-upload';
import { take } from 'rxjs';

interface IFileTypesDto {
    fileTypesLabel: string;
    accepts: string;
    dropZoneId: string;
    fileInputId: string;
    indexId: string;
}

type DefaultDocumentTypeDto
    = IDefaultSystemAreaDocumentTypeDto & IFileTypesDto;

@Component({
    selector: 'cb-request-preconsent-signed-scheme-upload',
    templateUrl: './request-preconsent-signed-scheme-upload.component.html',
    styleUrls: ['./request-preconsent-signed-scheme-upload.component.scss'],
    standalone: false
})

export class RequestPreconsentSignedSchemeUploadComponent implements OnInit {
    @Input() public mappedItem: IPreConsentPlanMappedItem;
    @Input() public childForm: UntypedFormGroup;
    @Input() public documentsToUpload: IRequestPreConsentPlanDocumentsToUpload;
    @Input() public lotId: number;
    @Input() public lotContractType: LotContractTypeEnumId;
    private readonly existingDocumentTypes = new Set<number>();
    public readonly defaultDocumentTypesIndex: { [documentTypeId_documentEntityId: string]: DefaultDocumentTypeDto } = {};
    public files: { [documentTypeId_documentEntityId: string]: File } = {};
    public defaultDocumentTypes: DefaultDocumentTypeDto[] = [];
    public readonly documentTypeRequired: { [documentTypeId: number]: boolean } = {};
    public readonly documentTypeFileErrors: { [documentTypeId_documentEntityId: string]: string[] } = {};

    public documentCount = 0;
    constructor(
        public readonly logicService: PreConsentPlansLogicService,
        private readonly defaultSystemAreaDocumentTypesLogic: DefaultSystemAreaDocumentTypesLogicService,
        public readonly lotDesignPermissions: LotDesignPermissions
    ) {
    }

    public ngOnInit(): void {
        this.mappedItem = this.logicService.$createMappedItem();
        this.loadDefaultDocumentTypes();
    }

    private loadDefaultDocumentTypes(): void {
        this.defaultSystemAreaDocumentTypesLogic
            .getListByUploadedSystemArea(this.lotContractType, SYSTEM_AREA_ENUM.DesignRequirements, SYSTEM_AREA_ENUM.PreConsentPlan, this.lotId, DOCUMENT_GROUP_CODES_CONST.DESIGN_INFO)
            .pipe(take(1)).subscribe((results) => {
                this.defaultDocumentTypes = results.map((docType: DefaultDocumentTypeDto) => ({ ...docType, ...this.mapFileTypes(docType) }));
                this.defaultDocumentTypes.forEach(docType => {
                    this.defaultDocumentTypesIndex[docType.indexId] = docType;
                    if (docType.existingDocument !== undefined) {
                        this.existingDocumentTypes.add(docType.documentTypeId);
                    }
                    this.setDocumentTypeRequired(docType);
                });
            });
    }

    public getFileName(documentType: DefaultDocumentTypeDto
    ): string | undefined {
        return this.files[documentType.indexId]?.name ?? documentType?.existingDocument?.name;
    }

    private mapFileTypes(docType: IDefaultSystemAreaDocumentTypeDto): IFileTypesDto {
        const indexId = `${docType.documentTypeId}_${docType?.existingDocument?.documentEntityId ?? 0}`;

        return {
            accepts: docType.fileTypes.map(fileType => `.${fileType}`).join(', '),
            dropZoneId: `file-dropzone-${indexId}`,
            fileInputId: `file-upload-${indexId}`,
            fileTypesLabel: docType.fileTypes.join(', '),
            indexId,
        };
    }

    public disableUpload(documentType: DefaultDocumentTypeDto
    ): boolean {
        if (documentType.existingDocument?.allowMultiples === false) {
            return false;
        }
        return !!this.files[documentType.indexId]
            || documentType.existingDocument?.allowMultiples;
    }

    public removeFile(documentType: DefaultDocumentTypeDto
    ): void {
        this.clearInputFile(documentType.fileInputId);
        delete this.files[documentType.indexId];
        this.setDocumentTypeRequired(documentType, true);
        this.mapUploadFilesToMappedItem();
    }

    triggerFileInput(fileInput: HTMLInputElement): void {
        if (fileInput) {
            fileInput.click();  // Trigger the click on the specific row's input
        }
    }

    @Debounce()
    public fileUploaded(indexId: string, event: Event): void {
        const input = event.target as HTMLInputElement;

        // Check if any file is selected
        if (!input.files || input.files.length === 0) {
            return;
        }

        const file = input.files[0];
        if (!this.validateFile(indexId, file)) {
            this.clearInputFile(this.defaultDocumentTypesIndex[indexId]?.fileInputId);
            return;
        }
        this.files[indexId] = file;
        this.setDocumentTypeRequired(this.defaultDocumentTypesIndex[indexId], false);
        this.mapUploadFilesToMappedItem();
    }

    /** @returns true for valid */
    private validateFile(indexId: string, file: File): boolean {
        this.documentTypeFileErrors[indexId] = [];
        let result = true;
        const documentType = this.defaultDocumentTypesIndex[indexId];

        const maxMB = documentType.maximumFileSizeInMegaBytes;
        const actualFileSizeMB = (file.size / 1024 / 1024); // Bytes to MB
        if (actualFileSizeMB > maxMB) {
            const fileSizeError = `Invalid File Size ${actualFileSizeMB.toFixed(2)}MB is greater than the maximum ${maxMB}MB`;
            this.documentTypeFileErrors[indexId].push(fileSizeError);
        }

        const fileExt = file.name.split('.').pop().toLowerCase();
        if (!documentType.fileTypes.some(fileType => fileType.toString().toLowerCase() === fileExt)) {
            const extError = `Invalid File type (${fileExt}) must be one of the following types; ${documentType.fileTypesLabel}`;
            this.documentTypeFileErrors[indexId].push(extError);
            result = false;
        }

        return result;
    }

    private mapUploadFilesToMappedItem(): void {
        this.documentsToUpload.designInfoDocuments = [];
        for (const indexId in this.files) {
            if (this.files.hasOwnProperty(indexId)) {
                const file = this.files[indexId];
                const documentType: DefaultDocumentTypeDto
                    = this.defaultDocumentTypesIndex[indexId];
                this.documentsToUpload.designInfoDocuments.push({
                    documentEntityId: documentType.existingDocument?.documentEntityId,
                    documentGroupId: documentType.documentGroupId,
                    documentTypeId: documentType.documentTypeId,
                    file,
                    name: file.name,
                } as IDocumentUploadDto);
            }
        }
    }

    private setDocumentTypeRequired(documentType: DefaultDocumentTypeDto, required = true): void {
        if (documentType.requiredType === DOCUMENT_REQUIRED_TYPE_ENUM.Required) {
            this.documentTypeRequired[documentType.documentTypeId]
                = this.existingDocumentTypes.has(documentType.documentTypeId) ? false : required;
        }
        const isValid = !Object.values(this.documentTypeRequired).some(x => x);
        if (isValid) {
            this.childForm.setErrors(null);
        } else {
            this.childForm.setErrors({ invalid: true });
        }
    }

    private clearInputFile(fileInputId: string): void {
        const input = document.getElementById(fileInputId) as HTMLInputElement;
        if (input) {
            input.value = '';
        }
    }
}
